import {
  getMachinesSearch,
  getMachinesSearchTagging,
} from "@aura-frontend/omnibus-az-client";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";

export type UseMachinesParams = {
  /** If true, only fetches machines with tagging capability. Default: true */
  isTaggingMachines?: boolean;
  /** Region to filter machines by */
  region?: string;
  /** Search query to filter machines by name (server-side, triggers auto-pagination) */
  searchQuery?: string;
  /** Whether to enable the query. Default: true */
  enabled?: boolean;
};

const extractRegionName = (region: string) => region.replace(/ \(.*\)$/, "");

const machinesInfiniteOptions = (query: { region?: string; name?: string }) =>
  infiniteQueryOptions({
    queryKey: ["getMachinesSearch", query],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getMachinesSearch({
        query: { ...query, continue: pageParam },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
  });

const taggingMachinesInfiniteOptions = (query: { name?: string }) =>
  infiniteQueryOptions({
    queryKey: ["getMachinesSearchTagging", query],
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getMachinesSearchTagging({
        query: { continue: pageParam, ...query },
        signal,
        throwOnError: true,
      });
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.continue,
  });

export function useMachines({
  isTaggingMachines = true,
  region,
  searchQuery,
  enabled = true,
}: UseMachinesParams = {}) {
  const trimmedSearch = searchQuery?.trim();
  const query = {
    ...(region && { region: extractRegionName(region).toLowerCase() }),
    ...(trimmedSearch && { name: trimmedSearch }),
  };

  const options = isTaggingMachines
    ? taggingMachinesInfiniteOptions(
        trimmedSearch ? { name: trimmedSearch } : {},
      )
    : machinesInfiniteOptions(query);

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...options,
    enabled,
  });

  const machines = data?.pages.flatMap((p) => p.machines ?? []) ?? [];

  return {
    machines,
    loadingMachines: isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    machinesError: error,
  } as const;
}
